import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { cn } from "@/lib/utils";

// A lightweight hover/focus tooltip — no Radix dependency. It renders into a
// body portal with fixed positioning, so it is NOT clipped by an ancestor's
// overflow (the collapsed sidebar scroll container would otherwise cut it off).
// Wraps its child in a block element used as the measuring anchor.
export function Tooltip({
  label,
  children,
  side = "right",
  disabled = false,
  delay = 150,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  side?: "right" | "top";
  disabled?: boolean;
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const show = () => {
    if (disabled) return;
    timer.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (side === "top") setPos({ top: r.top - 8, left: r.left + r.width / 2 });
      else setPos({ top: r.top + r.height / 2, left: r.right + 8 });
    }, delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setPos(null);
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div ref={ref} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} className={className}>
      {children}
      {pos &&
        !disabled &&
        createPortal(
          <div
            role="tooltip"
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              transform: side === "top" ? "translate(-50%, -100%)" : "translateY(-50%)",
            }}
            className={cn(
              "pointer-events-none z-[70] whitespace-nowrap rounded-md border bg-popover px-2 py-1 text-xs font-medium text-popover-foreground shadow-md",
            )}
          >
            {label}
          </div>,
          document.body,
        )}
    </div>
  );
}
